<template>
  <div class="as-a-coach-history-table">
    <div v-if="!loading && rows.length > 0" class="as-a-coach-history-table__search">
      <SearchBar
        label="Filter"
        ghostText="Search"
        :filterBtn="false"
        :valueSearch="searchQuery"
        class="flex-grow-1"
        removeColumnClass
        @onSearch="handleSearch"
      />
    </div>

    <SharedTableSkeleton
      v-if="loading"
      :rowCount="5"
      :columnCount="5"
      columnTemplate="2fr 2fr 3fr 1fr 48px"
    />

    <div v-else-if="paginatedRows.length > 0" class="as-a-coach-history-table__scroller">
      <table class="history-table">
        <caption class="history-table__caption">History of inspection requests</caption>
        <thead>
          <tr>
            <th scope="col" class="history-table__header">Requested by</th>
            <th scope="col" class="history-table__header">For level / Requested on</th>
            <th scope="col" class="history-table__header">For responsibility</th>
            <th scope="col" class="history-table__header">Outcome</th>
            <th scope="col" class="history-table__header history-table__header--icon">
              <span class="history-table__sr-only">Chatter</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in paginatedRows"
            :key="row.id"
            class="history-table__row"
            :class="{ 'history-table__row--expedited': row.expedited }"
          >
            <td class="history-table__cell">
              <EmployeeCard
                :firstName="row.employee.firstName"
                :lastName="row.employee.lastName"
                :photoUrl="row.employee.photoUrl"
                :showAvailability="false"
                :showImportanceSelector="false"
              />
            </td>

            <td class="history-table__cell">
              <div class="history-table__level-date">
                <MinimalisticReadinessLevel :level="row.targetLevel" />
                <span v-if="row.requestedOn" class="history-table__date">On {{ row.requestedOn }}</span>
                <span v-if="row.expedited" class="history-table__expedited">Expedited</span>
              </div>
            </td>

            <td class="history-table__cell">
              <ResponsibilityAssignmentDropDown
                :responsibilityCode="row.responsibility.code"
                :responsibilityName="row.responsibility.name"
                :show_codes="true"
                class="history-table__responsibility"
              />
            </td>

            <td class="history-table__cell history-table__outcome">
              <InspectionLevelBadge
                v-if="row.status === 'completed' && row.awardedLevel"
                :level="row.awardedLevel"
                :class="`history-table__level-pill history-table__level-pill--${row.awardedLevel.toLowerCase()}`"
              />
              <span v-else-if="row.status === 'completed'" class="history-table__completed">Completed</span>
              <InspectionRequestStatusBadge v-else :status="row.status" />
            </td>

            <td class="history-table__cell history-table__cell--icon">
              <button
                type="button"
                class="history-table__chatter"
                :aria-label="`Open chatter for ${row.employee.fullName}`"
                @click="handleChatterClick(row.source)"
              >
                <ChatterIcon />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <NoRecordFound
      v-else-if="rows.length > 0"
      title="Inspection Requests"
      message="Inspection Requests"
      btnTitle="Inspection Request"
      showSpecificMessage="No requests found"
      description="Your search did not match any requests.<br/>Please try again."
      :showClearAction="true"
      clearLabel="Clear search"
      :shouldShowAddAction="false"
      :showActiveCounter="false"
      :noBorder="true"
      @onClear="clearSearch"
    />
    <NoRecordFound
      v-else
      title="Inspection Requests"
      message="Inspection Requests"
      btnTitle="Inspection Request"
      showSpecificMessage="No requested inspections from you"
      description="No employee requested to be inspected by you"
      :shouldShowAddAction="false"
      :showActiveCounter="false"
      :showClearAction="false"
      :noBorder="true"
    />

    <div v-if="showPagination" class="as-a-coach-history-table__pagination">
      <ExPagination :pagination="pagination" @onRequest="handlePaginationRequest" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import ResponsibilityAssignmentDropDown from "@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue";
import MinimalisticReadinessLevel from "@components/MinimalisticReadinessLevel/MinimalisticReadinessLevel.vue";
import InspectionLevelBadge from "@components/InspectionLevelBadge/InspectionLevelBadge.vue";
import InspectionRequestStatusBadge from "@components/InspectionRequestStatusBadge/InspectionRequestStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";

const props = defineProps({
  /** Rows of the as-a-coach history feed (`inspection-requests/as-coach/history/`). */
  items: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["chatter-click"]);

const ITEMS_PER_PAGE = 10;
// `abandoned` is legacy: no new row reaches it, but old rows stay as read-only history.
const TERMINAL_STATUSES = ["completed", "failed", "canceled", "declined", "abandoned"];
const LEVEL_NAMES = ["apprentice", "professional", "coach", "master"];
const LEVEL_BY_BADGE = { 1: "apprentice", 2: "professional", 3: "coach", 4: "master" };

const searchQuery = ref("");
const currentPage = ref(1);

const toLevelName = (value) => {
  const name = String(value ?? "").toLowerCase();
  return LEVEL_NAMES.includes(name) ? name : null;
};

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const formatRequestedOn = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// The awarded level of a completed request: the issued badge, else the level recorded at completion.
const getAwardedLevel = (item) => {
  const name =
    LEVEL_BY_BADGE[item.badge_issued?.level] ||
    toLevelName(item.level_at_completion) ||
    toLevelName(item.badge_target_level);
  return name ? capitalize(name) : null;
};

const toRow = (item) => {
  const firstName = item.for_employee?.first_name || "";
  const lastName = item.for_employee?.last_name || "";
  return {
    id: item.id,
    status: item.status,
    expedited: Boolean(item.expedited),
    targetLevel: toLevelName(item.badge_target_level) || "apprentice",
    requestedOn: formatRequestedOn(item.created_on),
    awardedLevel: getAwardedLevel(item),
    employee: {
      firstName,
      lastName,
      photoUrl: item.for_employee?.avatar || undefined,
      fullName: `${firstName} ${lastName}`.trim(),
    },
    responsibility: {
      code: item.for_responsibility?.code || "",
      name: item.for_responsibility?.name || "",
    },
    source: item,
  };
};

const rows = computed(() =>
  props.items.filter((item) => TERMINAL_STATUSES.includes(item?.status)).map(toRow)
);

const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return rows.value;
  return rows.value.filter(
    (row) =>
      row.employee.fullName.toLowerCase().includes(query) ||
      row.responsibility.name.toLowerCase().includes(query) ||
      row.responsibility.code.toLowerCase().includes(query)
  );
});

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return filteredRows.value.slice(start, start + ITEMS_PER_PAGE);
});

const pagination = computed(() => ({
  page: currentPage.value,
  total: filteredRows.value.length,
  perPage: ITEMS_PER_PAGE,
}));

const showPagination = computed(() => !props.loading && filteredRows.value.length > ITEMS_PER_PAGE);

const handleSearch = (value) => {
  searchQuery.value = value || "";
  currentPage.value = 1;
};

const clearSearch = () => handleSearch("");

const handlePaginationRequest = (data) => {
  currentPage.value = data.page;
};

const handleChatterClick = (item) => {
  emit("chatter-click", item);
};

defineExpose({
  rows,
  filteredRows,
  currentPage,
  searchQuery,
  handleSearch,
  clearSearch,
  handlePaginationRequest,
  handleChatterClick,
});
</script>

<style scoped>
.as-a-coach-history-table {
  width: 100%;
  background: #ffffff;
  font-family: Inter, sans-serif;
}

.as-a-coach-history-table__search {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 20px;
}

.flex-grow-1 {
  flex-grow: 1;
}

.as-a-coach-history-table__scroller {
  overflow-x: auto;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.as-a-coach-history-table__pagination {
  padding: 12px 16px 16px;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table__caption,
.history-table__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.history-table__header {
  height: 44px;
  padding: 12px 24px;
  background-color: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  white-space: nowrap;
}

.history-table__cell {
  padding: 16px 12px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.history-table__cell:first-child {
  padding-left: 24px;
}

.history-table__row:last-child .history-table__cell {
  border-bottom: none;
}

.history-table__row--expedited {
  background-color: #fdf2fa;
}

.history-table__level-date {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.history-table__date {
  color: #4d5763;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
}

.history-table__expedited {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #fcceee;
  border-radius: 6px;
  background-color: #fdf2fa;
  color: #c11574;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.history-table__completed {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
}

.history-table__outcome .history-table__level-pill--apprentice {
  color: #b54708;
}

.history-table__outcome .history-table__level-pill--professional {
  color: #067647;
}

.history-table__outcome .history-table__level-pill--coach {
  color: #0062a3;
}

.history-table__cell--icon {
  width: 48px;
  padding-right: 24px;
}

.history-table__chatter {
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.history-table__chatter:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.history-table__responsibility {
  width: 100%;
}
</style>
